import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Toast from '@/shared/components/Toast';

import { ReactNode } from "react";

interface ToastMessage {
  id: string;
  message: ReactNode;
  type: 'success' | 'error' | 'info';
  details: string[];
  duration: number;
}

interface ToastContextType {
  showToast: (message: ReactNode, type?: 'success' | 'error' | 'info', details?: string[], duration?: number, toastId?: string) => void;
  dismissToast?: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [toastRoot, setToastRoot] = useState<HTMLElement | null>(null);
  const toastCounterRef = useRef(0);

  useEffect(() => {
    let root = document.getElementById('toast-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'toast-root';
      document.body.appendChild(root);
    }
    setToastRoot(root);
  }, []);

  const showToast = useCallback((message: ReactNode, type: 'success' | 'error' | 'info' = 'success', details: string[] = [], duration: number = 3000, toastId?: string) => {
    toastCounterRef.current += 1;
    const id = toastId ?? `${Date.now()}-${toastCounterRef.current}`;
    setToasts((prev) => {
      // If a toast with the same id already exists, replace it (deduplication)
      const exists = toastId ? prev.some((t) => t.id === id) : false;
      if (exists) {
        return prev.map((t) => t.id === id ? { id, message, type, details, duration } : t);
      }
      return [...prev, { id, message, type, details, duration }];
    });
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast])}>
      {children}
      {toastRoot && ReactDOM.createPortal(
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[10002] space-y-2 flex flex-col items-center">
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              details={toast.details}
              duration={toast.duration}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>,
        toastRoot
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
